import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  ChevronLeft,
  ChevronRight,
  RotateCcw,
  LogIn,
  Share2,
  Printer,
} from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { toB64Obj, toDataUri } from "../../utils/fileUtils";
import useAgentExec from "../../hooks/agent/useAgentExec";
import RelationsSection from "./RelationsSection";
import AivySideBanner from "../common/AivySideBanner";
import DaiumCareBanner from "../common/DaiumCareBanner";
import ShareModal from "../common/ShareModal";
import { useIsAuthenticated } from "../../store/authStore";
import { useNavigate } from "react-router-dom";

interface AgentRelation {
  agent_id: string;
  agent_title: string;
  agent_image: string;
  agent_description: string;
  agent_kind: string;
  agent_schema: AgentSchema;
  child_license: boolean;
}

const slideAnimationStyles = `
  @keyframes slideInLeft {
    from {
      transform: translateX(-100%);
      opacity: 0;
    }
    to {
      transform: translateX(0);
      opacity: 1;
    }
  }
  
  @keyframes slideInRight {
    from {
      transform: translateX(100%);
      opacity: 0;
    }
    to {
      transform: translateX(0);
      opacity: 1;
    }
  }
  
  @keyframes slideOutLeft {
    from {
      transform: translateX(0);
      opacity: 1;
    }
    to {
      transform: translateX(-100%);
      opacity: 0;
    }
  }
  
  @keyframes slideOutRight {
    from {
      transform: translateX(0);
      opacity: 1;
    }
    to {
      transform: translateX(100%);
      opacity: 0;
    }
  }
`;

if (
  typeof document !== "undefined" &&
  !document.querySelector("#slide-animations")
) {
  const style = document.createElement("style");
  style.id = "slide-animations";
  style.textContent = slideAnimationStyles;
  document.head.appendChild(style);
}

interface AgentSchema {
  type: string;
  properties: {
    [key: string]: {
      type: string;
      description: string;
      input_kind: string;
      options?: string[];
      accept?: string[];
      items?: {
        enum?: string[];
      };
    };
  };
  required: string[];
}

interface Agent {
  agent_id: string;
  agent_image: string;
  agent_title: string;
  agent_kind: string;
  agent_description: string;
  agent_schema: AgentSchema;
  child_license: boolean;
}

interface AgentPanelProps {
  agent: Agent;
  relations?: AgentRelation[];
}

const AgentPanel = ({ agent, relations }: AgentPanelProps) => {
  const navigate = useNavigate();
  const isAuthenticated = useIsAuthenticated();
  const [inputs, setInputs] = useState<{ [key: string]: any }>({});
  const [result, setResult] = useState("");
  const [imgs, setImgs] = useState<(string | { data: string; ext: string })[]>(
    []
  );
  const [currentStep, setCurrentStep] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [slideDirection, setSlideDirection] = useState<"next" | "prev">("next");
  const [isTyping, setIsTyping] = useState(false);
  const [loadingStep, setLoadingStep] = useState(0);
  const [isServerResponseReceived, setIsServerResponseReceived] =
    useState(false);
  const [loadingStartTime, setLoadingStartTime] = useState<number>(0);
  const [showResult, setShowResult] = useState(false);
  const [showImages, setShowImages] = useState(false);
  const [visibleImageCount, setVisibleImageCount] = useState(0);
  const [sharedSid, setSharedSid] = useState<string | null>(null);
  const [showShareModal, setShowShareModal] = useState(false);
  const typingTimerRef = useRef<NodeJS.Timeout | null>(null);
  const loadingTimerRef = useRef<NodeJS.Timeout | null>(null);
  const imageTimerRef = useRef<NodeJS.Timeout | null>(null);
  const { execute, loading, error, clearError } = useAgentExec();

  // 공유 기능
  const handleShare = () => {
    if (!sharedSid) return;
    setShowShareModal(true);
  };

  // 인쇄 기능
  const handlePrint = () => {
    const printWindow = window.open("", "_blank");

    const imagesHtml = imgs
      .map((img, idx) => {
        const imgSrc =
          typeof img === "string"
            ? img
            : typeof img === "object"
            ? toDataUri(img.data)
            : toDataUri(img);

        return `
        <div style="margin-bottom: 20px; text-align: center;">
          <img src="${imgSrc}" style="max-width: 100%; height: 100%; display: block; margin: 0 auto;" />
        </div>
      `;
      })
      .join("");

    printWindow?.document.write(`
      <html>
        <head>
          <title>이미지 인쇄</title>
          <style>
            * {
              margin: 0;
              padding: 0;
              box-sizing: border-box;
            }
            body {
              padding: 15px;
              font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
              display: flex;
              flex-direction: column;
              align-items: center;
            }
            @media print {
              body { 
                padding: 10px;
              }
              @page {
                size: A4;
                margin: 10mm;
              }
              /* 브라우저 기본 헤더/푸터 제거 */
              @page {
                margin-top: 0;
                margin-bottom: 0;
              }
              body {
                margin: 0;
              }
              img {
                max-width: 100%;
                height: 100%;
              }
            }
          </style>
        </head>
        <body>
          ${imagesHtml}
          <script>
            window.onload = function() {
              setTimeout(function() {
                window.print();
                window.close();
              }, 250);
            };
          </script>
        </body>
      </html>
    `);

    printWindow?.document.close();
  };

  const loadingSteps = [
    "데이터를 처리하는 중이에요...",
    "데이터를 분석 중이에요...",
    "결과를 작성 중이에요...",
  ];

  // 필수 입력 필드
  const requiredFields = useMemo(
    () => agent.agent_schema.required || [],
    [agent.agent_schema.required]
  );
  const canExec = requiredFields.every((key) => {
    const v = inputs[key];
    return v !== undefined && v !== "" && !(Array.isArray(v) && v.length === 0);
  });

  // 입력 필드 초기화
  useEffect(() => {
    setCurrentStep(0);
    setIsAnimating(false);
    setIsTyping(false);
    setLoadingStep(0);
    setIsServerResponseReceived(false);
    setLoadingStartTime(0);
    setShowResult(false);
    setShowImages(false);
    setVisibleImageCount(0);
    setSharedSid(null);

    if (typingTimerRef.current) {
      clearInterval(typingTimerRef.current);
    }
    if (loadingTimerRef.current) {
      clearInterval(loadingTimerRef.current);
    }
    if (imageTimerRef.current) {
      clearTimeout(imageTimerRef.current);
    }

    const init = requiredFields.reduce((acc, key) => {
      const prop = agent.agent_schema.properties[key];
      if (prop && prop.input_kind === "multiselect") {
        return { ...acc, [key]: [] };
      }
      return { ...acc, [key]: "" };
    }, {});
    setInputs(init);
    setResult("");
    setImgs([]);
    setSharedSid(null);
    setShowShareModal(false);
    clearError();
  }, [agent, requiredFields, clearError]);

  const change = (k: string, v: any) => setInputs((p) => ({ ...p, [k]: v }));

  const fileIn = async (k: string, f: File | null) =>
    f && change(k, await toB64Obj(f));

  // 타이핑 애니메이션
  const typeOut = (
    text: string,
    images?: (string | { data: string; ext: string })[]
  ) => {
    setIsTyping(true);
    setResult("");

    if (typingTimerRef.current) {
      clearInterval(typingTimerRef.current);
    }

    let currentIndex = 0;
    const typeSpeed = 6;

    typingTimerRef.current = setInterval(() => {
      if (currentIndex < text.length) {
        setResult(text.slice(0, currentIndex + 1));
        currentIndex++;
      } else {
        clearInterval(typingTimerRef.current!);
        setIsTyping(false);
        // 타이핑 완료 후 이미지 표시 시작
        if (images && images.length > 0) {
          setTimeout(() => {
            setShowImages(true);
            startImageSequence(images);
          }, 500);
        }
      }
    }, typeSpeed);
  };

  // 이미지 순차 표시
  const startImageSequence = (
    imageList?: (string | { data: string; ext: string })[]
  ) => {
    const imagesToShow = imageList || imgs;
    if (imagesToShow.length === 0) return;

    setVisibleImageCount(0);

    if (imageTimerRef.current) {
      clearTimeout(imageTimerRef.current);
    }

    const showNextImage = (index: number) => {
      if (index < imagesToShow.length) {
        setVisibleImageCount(index + 1);
        imageTimerRef.current = setTimeout(() => {
          showNextImage(index + 1);
        }, 600);
      }
    };

    showNextImage(0);
  };

  useEffect(() => {
    return () => {
      if (typingTimerRef.current) {
        clearInterval(typingTimerRef.current);
      }
      if (loadingTimerRef.current) {
        clearInterval(loadingTimerRef.current);
      }
      if (imageTimerRef.current) {
        clearTimeout(imageTimerRef.current);
      }
    };
  }, []);

  // 로딩 스텝 시작
  const startLoadingSteps = () => {
    setLoadingStep(0);
    setIsServerResponseReceived(false);
    setLoadingStartTime(Date.now());

    if (loadingTimerRef.current) {
      clearInterval(loadingTimerRef.current);
    }

    loadingTimerRef.current = setInterval(() => {
      setLoadingStep((prev) => {
        if (prev < loadingSteps.length - 1) {
          return prev + 1;
        }
        return prev;
      });
    }, 3000);
  };

  // 로딩 스텝 완료
  const completeLoadingSteps = () => {
    const actualResponseTime = Date.now() - loadingStartTime;
    const timePerStep = actualResponseTime / loadingSteps.length;

    setIsServerResponseReceived(true);

    if (loadingTimerRef.current) {
      clearInterval(loadingTimerRef.current);
    }

    // 실제 응답 시간을 기준으로 각 단계별 시간 계산
    // 남은 단계들을 실제 시간 비율에 맞춰 진행
    if (loadingStep < loadingSteps.length - 1) {
      let currentStep = loadingStep;
      const progressSteps = () => {
        currentStep++;
        if (currentStep < loadingSteps.length) {
          setLoadingStep(currentStep);
          const adjustedTime = Math.min(Math.max(timePerStep * 0.3, 500), 1000);
          setTimeout(progressSteps, adjustedTime);
        }
      };

      // 첫 번째 남은 단계 시작
      const adjustedTime = Math.min(Math.max(timePerStep * 0.3, 500), 1000);
      setTimeout(progressSteps, adjustedTime);
    }
  };

  // Agent 실행 함수
  const exec = async () => {
    setShowResult(true);
    startLoadingSteps();

    const result = await execute({
      agent_id: agent.agent_id,
      agent_kind: agent.agent_kind,
      input_values: inputs,
    });

    const actualResponseTime = Date.now() - loadingStartTime;
    completeLoadingSteps();

    // 실제 응답 시간에 비례하여 결과 표시 대기 시간 조정
    const timePerStep = actualResponseTime / loadingSteps.length;
    const remainingSteps = loadingSteps.length - loadingStep - 1;
    const waitTime =
      remainingSteps > 0
        ? remainingSteps * Math.min(Math.max(timePerStep * 0.3, 500), 1000) +
          300
        : 300;

    setTimeout(() => {
      if (result) {
        const { generated_text, generated_images, shared_sid } = result;

        // shared_sid 저장
        if (shared_sid) {
          setSharedSid(shared_sid);
        }

        if (Array.isArray(generated_images) && generated_images.length > 0) {
          setImgs(generated_images);
        }

        if (generated_text?.trim()) {
          typeOut(generated_text, generated_images);
        } else if (
          Array.isArray(generated_images) &&
          generated_images.length > 0
        ) {
          setTimeout(() => {
            setShowImages(true);
            startImageSequence(generated_images);
          }, 500);
        }

        if (
          !generated_text?.trim() &&
          (!Array.isArray(generated_images) || generated_images.length === 0)
        ) {
          setResult("출력에 실패했습니다.");
        }
      }
    }, waitTime);
  };

  const resetToInput = () => {
    setShowResult(false);
    setCurrentStep(0);
    setResult("");
    setImgs([]);
    setShowImages(false);
    setVisibleImageCount(0);
    setSharedSid(null);
    setShowShareModal(false);
    clearError();

    if (typingTimerRef.current) {
      clearInterval(typingTimerRef.current);
    }
    if (loadingTimerRef.current) {
      clearInterval(loadingTimerRef.current);
    }
    if (imageTimerRef.current) {
      clearTimeout(imageTimerRef.current);
    }
  };

  // 입력 필드 초기화
  const entries = requiredFields
    .map((key) => [key, agent.agent_schema.properties[key]])
    .filter(([_, prop]) => prop);
  const totalSteps = entries.length;
  const currentEntry = entries[currentStep] as [string, any] | undefined;

  // 현재 스텝 완료 여부 확인
  const isCurrentStepComplete = () => {
    if (!currentEntry) return false;
    const [key, prop] = currentEntry;
    const isRequired = requiredFields.includes(key);
    if (!isRequired) return true;

    const value = inputs[key];

    if (prop && prop.input_kind === "multiselect") {
      return Array.isArray(value) && value.length > 0;
    }

    return (
      value !== undefined &&
      value !== "" &&
      !(Array.isArray(value) && value.length === 0)
    );
  };

  const nextStep = () => {
    if (currentStep < totalSteps - 1 && !isAnimating) {
      setSlideDirection("next");
      setIsAnimating(true);

      const timeout1 = setTimeout(() => {
        setCurrentStep(currentStep + 1);
        const timeout2 = setTimeout(() => {
          setIsAnimating(false);
        }, 300);
        return () => clearTimeout(timeout2);
      }, 150);
      return () => clearTimeout(timeout1);
    }
  };

  const prevStep = () => {
    if (currentStep > 0 && !isAnimating) {
      setSlideDirection("prev");
      setIsAnimating(true);

      const timeout1 = setTimeout(() => {
        setCurrentStep(currentStep - 1);
        const timeout2 = setTimeout(() => {
          setIsAnimating(false);
        }, 300);
        return () => clearTimeout(timeout2);
      }, 150);
      return () => clearTimeout(timeout1);
    }
  };

  const progressBar = () => {
    return (
      <div className="w-full h-2 bg-slate-200 rounded-full">
        <div
          className="h-full bg-gradient-to-r from-blue-400 via-blue-500 to-blue-600 rounded-full"
          style={{ width: `${((currentStep + 1) / totalSteps) * 100}%` }}
        ></div>
      </div>
    );
  };

  // 결과 화면 렌더링
  if (showResult) {
    return (
      <>
        <AivySideBanner />
        <DaiumCareBanner />
        <div className="max-w-7xl mx-auto font-serif-kr">
          <div className="flex justify-between items-center mb-10">
            <div className="flex items-center gap-4 flex-1 min-w-0">
              <div className="relative">
                <img
                  src={agent.agent_image}
                  alt={agent.agent_title}
                  className="w-16 h-16 rounded-xl object-cover ring-1 ring-slate-200 shadow-sm"
                />
                {loading && (
                  <div className="absolute -top-1 -right-1 w-4 h-4 bg-blue-500 rounded-full flex items-center justify-center">
                    <div className="w-2 h-2 bg-white rounded-full animate-pulse"></div>
                  </div>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <h1 className="text-lg md:text-2xl font-semibold text-slate-900  tracking-tight overflow-hidden text-ellipsis whitespace-nowrap">
                  {agent.agent_title}
                </h1>
                <p className="text-sm font-medium text-slate-500 mt-1">
                  {loading ? "처리 중..." : "작업 완료"}
                </p>
              </div>
              <button
                onClick={resetToInput}
                disabled={loading}
                className="inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 hover:border-slate-400 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-sm"
              >
                <RotateCcw className="w-4 h-4" />
                새로 시작
              </button>
            </div>
          </div>

          {/* 로딩 중일 때 로딩 단계 표시 */}
          {loading && (
            <div className="mb-10">
              <div className="bg-gray-50 rounded-lg px-6 md:px-10 border border-gray-200 h-[600px] flex items-center justify-center">
                <div className="text-center">
                  <div className="flex justify-center mb-8">
                    <div className="relative">
                      <div className="w-8 h-8 border-2 border-gray-300 border-t-gray-800 rounded-full animate-spin"></div>
                    </div>
                  </div>

                  <div>
                    <h2 className="text-xl font-semibold text-gray-800 mb-2 animate-pulse">
                      {loadingSteps[loadingStep]}
                    </h2>
                    <p className="text-sm text-gray-500">잠시만 기다려주세요</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {!loading && (
            <div className="space-y-8">
              {error && (
                <div className="p-6 bg-red-50 border border-red-200 rounded-xl">
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                      <span className="text-red-600 text-lg">⚠</span>
                    </div>
                    <div className="flex-1">
                      <h3 className="text-red-900 font-semibold text-lg mb-2">
                        처리 중 오류가 발생했습니다
                      </h3>
                      <p className="text-red-700 leading-relaxed">{error}</p>
                    </div>
                  </div>
                </div>
              )}

              {result && (
                <div className="bg-white border border-slate-200 rounded-xl shadow-sm">
                  <div className="px-6 md:px-8 py-6 border-b border-slate-100">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <span className="text-white text-[20px]">📄</span>
                        <h2 className="text-xl font-semibold text-slate-900 tracking-tight">
                          생성된 결과
                        </h2>
                        {isTyping && (
                          <div className="flex items-center gap-1 text-slate-400 ml-2">
                            <div className="w-1.5 h-1.5 bg-current rounded-full animate-bounce"></div>
                            <div
                              className="w-1.5 h-1.5 bg-current rounded-full animate-bounce"
                              style={{ animationDelay: "0.1s" }}
                            ></div>
                            <div
                              className="w-1.5 h-1.5 bg-current rounded-full animate-bounce"
                              style={{ animationDelay: "0.2s" }}
                            ></div>
                          </div>
                        )}
                      </div>
                      {sharedSid && (
                        <button
                          onClick={handleShare}
                          className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 hover:border-slate-400 transition-all duration-200 shadow-sm"
                        >
                          <Share2 className="w-4 h-4" />
                          공유하기
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="px-8 py-6">
                    <div className="prose prose-slate max-w-none text-slate-700 leading-snug prose-headings:text-slate-900 prose-p:text-slate-600 prose-strong:text-slate-800 prose-code:text-slate-800 prose-code:bg-slate-100 prose-pre:bg-slate-50 whitespace-pre-line">
                      <ReactMarkdown remarkPlugins={[remarkGfm]}>
                        {result}
                      </ReactMarkdown>
                    </div>
                  </div>
                </div>
              )}

              {showImages && imgs.length > 0 && (
                <div className="print-images-container bg-white border border-slate-200 rounded-xl shadow-sm">
                  <div className="px-8 py-6 border-b border-slate-100">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <span className="text-white text-[20px]">🖼</span>
                        <h2 className="text-xl font-semibold text-slate-900 tracking-tight">
                          생성된 이미지
                        </h2>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="bg-slate-100 px-3 py-1.5 rounded-lg text-sm font-medium text-slate-600">
                          {visibleImageCount} / {imgs.length}개
                        </div>
                        <button
                          onClick={handlePrint}
                          className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 hover:border-slate-400 transition-all duration-200 shadow-sm"
                        >
                          <Printer className="w-4 h-4" />
                          인쇄
                        </button>
                      </div>
                    </div>
                  </div>
                  <div className="p-8">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {imgs.slice(0, visibleImageCount).map((img, idx) => (
                        <div
                          key={idx}
                          className="print-image-item group bg-slate-50 rounded-xl p-4 border border-slate-200 hover:border-slate-300 transition-all duration-200 animate-in fade-in slide-in-from-bottom-4"
                          style={{ animationDelay: `${idx * 0.1}s` }}
                        >
                          <div className="print-image-number text-xs font-medium text-slate-500 mb-2">
                            이미지 {idx + 1} / {imgs.length}
                          </div>
                          <img
                            src={
                              typeof img === "string"
                                ? img
                                : typeof img === "object"
                                ? toDataUri(img.data)
                                : toDataUri(img)
                            }
                            alt={`result-${idx}`}
                            className="w-full h-auto rounded-lg shadow-sm"
                          />
                          <div className="mt-3 text-center">
                            <span className="text-xs font-medium text-slate-500 tracking-wide">
                              이미지 {idx + 1}
                            </span>
                          </div>
                        </div>
                      ))}

                      {visibleImageCount < imgs.length && (
                        <div className="group bg-slate-50 rounded-xl p-4 border border-slate-200 border-dashed">
                          <div className="w-full aspect-square bg-slate-100 rounded-lg flex items-center justify-center">
                            <div className="flex flex-col items-center gap-2">
                              <div className="w-6 h-6 border-2 border-slate-300 border-t-slate-600 rounded-full animate-spin"></div>
                              <span className="text-xs text-slate-500">
                                생성 중...
                              </span>
                            </div>
                          </div>
                          <div className="mt-3 text-center">
                            <span className="text-xs font-medium text-slate-400 tracking-wide">
                              이미지 {visibleImageCount + 1}
                            </span>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {!error && !result && imgs.length === 0 && (
                <div className="text-center py-16 h-[600px] flex flex-col justify-center">
                  <div className="flex justify-center mb-6">
                    <div className="flex space-x-1.5">
                      <div className="w-2.5 h-2.5 bg-slate-400 rounded-full animate-bounce"></div>
                      <div
                        className="w-2.5 h-2.5 bg-slate-400 rounded-full animate-bounce"
                        style={{ animationDelay: "0.1s" }}
                      ></div>
                      <div
                        className="w-2.5 h-2.5 bg-slate-400 rounded-full animate-bounce"
                        style={{ animationDelay: "0.2s" }}
                      ></div>
                    </div>
                  </div>
                  <p className="text-slate-500 text-lg font-light tracking-wide animate-pulse">
                    생성 결과를 준비하고 있습니다
                  </p>
                </div>
              )}
            </div>
          )}

          <RelationsSection relations={relations} />
        </div>

        <ShareModal
          isOpen={showShareModal}
          onClose={() => setShowShareModal(false)}
          shareUrl={`${window.location.origin}/agent/share/${sharedSid}`}
          title="AI 분석 결과 공유"
        />
      </>
    );
  }

  return (
    <>
      <AivySideBanner />
      <DaiumCareBanner />
      <div className="max-w-7xl mx-auto font-serif-kr">
        <div className="p-8 rounded-xl border border-slate-200 mx-auto bg-white shadow-sm mb-5">
          <div className="flex flex-col gap-4">
            <h1 className="text-xl md:text-2xl font-semibold text-slate-900 tracking-tight">
              {agent.agent_title}
            </h1>
            <div className="flex items-center gap-3">
              <img
                src={agent.agent_image}
                alt={agent.agent_title}
                className="w-14 h-14 rounded-xl object-cover ring-1 ring-slate-200 shadow-sm"
              />
              <p className="text-base md:text-lg font-medium text-slate-500">
                {agent.agent_description}
              </p>
            </div>
            <div className="flex flex-col gap-3">
              <div className="flex items-center justify-between">
                <p className="text-sm font-semibold text-slate-500">
                  진행 상황
                </p>
                <span className="text-sm font-semibold text-slate-500">
                  {currentStep + 1} / {totalSteps} 단계
                </span>
              </div>
              {progressBar()}
            </div>
          </div>
        </div>

        <div className="relative">
          {/* 비로그인 사용자를 위한 블러 오버레이 */}
          {!isAuthenticated && (
            <div className="absolute inset-0 z-50 flex items-center justify-center">
              <div className="absolute inset-0 bg-white/80 backdrop-blur-sm"></div>
              <div className="relative z-10 bg-white rounded-2xl shadow-2xl px-6 py-6  max-w-md text-center border border-slate-200">
                <div className="md:w-16 md:h-16 w-10 h-10 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <LogIn className="md:w-8 md:h-8 w-6 h-6 text-slate-600" />
                </div>
                <h2 className="md:text-xl text-lg font-semibold text-slate-900 mb-3">
                  로그인이 필요한 서비스입니다
                </h2>
                <p className="md:text-base text-[13px] text-slate-600 mb-6 leading-relaxed whitespace-pre-wrap">
                  {`이 서비스를 이용하시려면 로그인이 필요합니다.\n로그인 후 다양한 AI 에이전트를 경험해보세요.`}
                </p>
                <button
                  onClick={() => navigate("/auth/signin")}
                  className="md:w-full w-1/2 bg-slate-900 text-white py-3 px-4 rounded-xl font-medium md:text-base text-sm hover:bg-slate-800 transition-colors shadow-sm"
                >
                  로그인하러 가기
                </button>
              </div>
            </div>
          )}

          <div
            className={`px-10 py-14 rounded-xl border border-slate-200 mx-auto bg-white shadow-sm ${
              !isAuthenticated ? "blur-sm" : ""
            }`}
          >
            {currentEntry && (
              <div className="mb-8 overflow-hidden relative">
                <div
                  className={`flex flex-col p-2 transition-all duration-300 ease-in-out ${
                    isAnimating
                      ? slideDirection === "next"
                        ? "transform -translate-x-full opacity-0"
                        : "transform translate-x-full opacity-0"
                      : "transform translate-x-0 opacity-100"
                  }`}
                >
                  {(() => {
                    const [key, prop] = currentEntry;
                    if (!prop) return null;

                    const isReq = requiredFields.includes(key);
                    const kind = prop.input_kind || "text";
                    const accept =
                      (prop.accept &&
                        prop.accept.map((e: string) => "." + e).join(",")) ||
                      (kind === "audio"
                        ? ".mp3,.wav,.m4a,.mp4"
                        : ".png,.jpg,.jpeg");

                    return (
                      <div className="space-y-6">
                        <div>
                          <h2 className="text-lg md:text-2xl font-semibold mb-3 tracking-tight pb-2">
                            <span className="mr-2">Q{currentStep + 1}.</span>
                            {prop.description}
                            {isReq && (
                              <span className="text-red-500 ml-2">*</span>
                            )}
                          </h2>
                          <div className="flex flex-col items-start gap-2">
                            {kind === "multiselect" && (
                              <p className="text-base font-semibold">
                                해당되는 항목을 모두 선택해주세요
                              </p>
                            )}

                            {isReq && (
                              <p className="text-base md:text-lg text-red-500 font-semibold">
                                * 필수 항목
                              </p>
                            )}
                          </div>
                        </div>

                        {kind === "select" && (
                          <div className="space-y-3 max-h-80 overflow-y-auto scrollbar-hover-stable pr-4">
                            {prop.options?.map((option: string) => (
                              <button
                                key={option}
                                type="button"
                                onClick={() => change(key, option)}
                                className={`w-full text-left p-5 rounded-xl border-2 transition-all duration-200 ${
                                  inputs[key] === option
                                    ? "border-slate-300 bg-white text-slate-900 shadow-sm"
                                    : "border-slate-200 bg-white hover:border-slate-300 hover:shadow-sm"
                                }`}
                              >
                                <div className="flex items-center justify-between">
                                  <span className="text-sm md:text-base font-medium tracking-wide">
                                    {option}
                                  </span>
                                  <div
                                    className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-colors ${
                                      inputs[key] === option
                                        ? "border-slate-600 bg-slate-600"
                                        : "border-slate-300"
                                    }`}
                                  >
                                    {inputs[key] === option && (
                                      <div className="w-2 h-2 bg-white rounded-full"></div>
                                    )}
                                  </div>
                                </div>
                              </button>
                            ))}
                          </div>
                        )}

                        {kind === "multiselect" && (
                          <div className="space-y-3 max-h-80 overflow-y-auto scrollbar-hover-stable pr-4">
                            {(prop.options || prop.items?.enum)?.map(
                              (option: string) => {
                                const currentValues = inputs[key] || [];
                                const isSelected =
                                  currentValues.includes(option);

                                return (
                                  <button
                                    key={option}
                                    type="button"
                                    onClick={() => {
                                      const currentValues = inputs[key] || [];
                                      if (isSelected) {
                                        change(
                                          key,
                                          currentValues.filter(
                                            (v: string) => v !== option
                                          )
                                        );
                                      } else {
                                        change(key, [...currentValues, option]);
                                      }
                                    }}
                                    className={`w-full text-left p-5 rounded-xl border-2 transition-all duration-200 ${
                                      isSelected
                                        ? "border-slate-300 bg-white text-slate-900 shadow-sm"
                                        : "border-slate-200 bg-white hover:border-slate-300 hover:shadow-sm"
                                    }`}
                                  >
                                    <div className="flex items-center justify-between">
                                      <span className="text-sm md:text-base font-medium tracking-wide">
                                        {option}
                                      </span>
                                      <div
                                        className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                                          isSelected
                                            ? "border-slate-600 bg-slate-600"
                                            : "border-slate-300"
                                        }`}
                                      >
                                        {isSelected && (
                                          <svg
                                            className="w-3 h-3 text-white"
                                            fill="currentColor"
                                            viewBox="0 0 20 20"
                                          >
                                            <path
                                              fillRule="evenodd"
                                              d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                              clipRule="evenodd"
                                            />
                                          </svg>
                                        )}
                                      </div>
                                    </div>
                                  </button>
                                );
                              }
                            )}
                            {inputs[key] && inputs[key].length > 0 && (
                              <div className="mt-4 p-4 bg-white border border-slate-200 rounded-xl">
                                <p className="text-sm font-medium text-slate-700">
                                  ✓ {inputs[key].length}개 항목 선택됨:{" "}
                                  {inputs[key].join(", ")}
                                </p>
                              </div>
                            )}
                          </div>
                        )}

                        {kind === "text" && (
                          <textarea
                            required={isReq}
                            rows={5}
                            value={inputs[key] || ""}
                            onChange={(e) => change(key, e.target.value)}
                            className="w-full border border-slate-300 rounded-xl px-5 py-4 focus:outline-none focus:ring-2 focus:ring-slate-500 focus:border-transparent resize-none text-base placeholder-slate-400 bg-white"
                            placeholder="내용을 입력해주세요..."
                          />
                        )}

                        {["audio", "image"].includes(kind) && (
                          <div className="space-y-4">
                            <input
                              required={isReq}
                              type="file"
                              accept={accept}
                              onChange={(e) =>
                                fileIn(key, e.target.files?.[0] || null)
                              }
                              className="w-full text-sm text-slate-500 file:mr-4 file:py-3 file:px-5 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-slate-100 file:text-slate-700 hover:file:bg-slate-200 transition-colors"
                            />
                            {inputs[key] && (
                              <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-xl">
                                <p className="text-sm font-medium text-emerald-700">
                                  ✓ 파일이 선택되었습니다 ({inputs[key].ext}{" "}
                                  형식)
                                </p>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })()}
                </div>
              </div>
            )}

            <div className="flex justify-between items-center">
              <button
                onClick={prevStep}
                disabled={currentStep === 0 || isAnimating}
                className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 hover:border-slate-400 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-sm"
              >
                <ChevronLeft className="w-4 h-4" />
                이전 단계
              </button>

              {currentStep < totalSteps - 1 ? (
                <button
                  onClick={nextStep}
                  disabled={!isCurrentStepComplete() || isAnimating}
                  className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-medium text-white bg-slate-900 border border-slate-900 rounded-lg hover:bg-slate-800 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-sm"
                >
                  다음 단계
                  <ChevronRight className="w-4 h-4" />
                </button>
              ) : (
                <button
                  onClick={exec}
                  disabled={loading || !canExec || isAnimating}
                  className="inline-flex items-center gap-2 px-6 py-2.5 text-sm font-medium text-white bg-slate-900 border border-slate-900 rounded-lg hover:bg-slate-800 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-sm"
                >
                  실행하기
                </button>
              )}
            </div>
          </div>
        </div>

        <RelationsSection relations={relations} />
      </div>
    </>
  );
};

export default AgentPanel;
